import React, { FC, useCallback, useMemo } from "react";

import Tabs from "antd/es/tabs";
import { useIntl } from "react-intl";
import { useLocation } from "react-router";
import { useHistory } from "react-router-dom";

import { ReactComponent as DashboardIcon } from "@app/assets/dashboard/view-dashboard.svg";
import { ReactComponent as TimelineIcon } from "@app/assets/dashboard/view-timeline.svg";
import ViewType, { getViewTypeDisplayName } from "@app/scene/dashboard/enum/ViewType";
import useAnalytics from "@app/util/contexts/useAnalytics";

import styles from "./DashboardTabs.module.scss";

interface Props {
  view: ViewType;
}

const DashboardTabs: FC<Props> = ({ view }) => {
  const analytics = useAnalytics();
  const intl = useIntl();
  const history = useHistory();
  const location = useLocation();

  const items = useMemo(
    () => [
      {
        key: ViewType.DASHBOARD,
        label: (
          <span className={styles.option}>
            <DashboardIcon className={styles.icon} />
            {getViewTypeDisplayName(intl, ViewType.DASHBOARD)}
          </span>
        ),
      },
      {
        key: ViewType.TIMELINE,
        label: (
          <span className={styles.option}>
            <TimelineIcon className={styles.icon} />
            {getViewTypeDisplayName(intl, ViewType.TIMELINE)}
          </span>
        ),
      },
    ],
    [intl]
  );

  const handleChange = useCallback(
    (newView: string) => {
      analytics.clicked([`Toggle ${newView === ViewType.DASHBOARD ? "dashboard" : "timeline"}`]);
      const parts = location.pathname.split("/");
      if (
        // Only remove Dashboard part if there's more than 1 item in pathname
        // Notes:
        //   1. We use 2, because "/abc" string results in two array items after split.
        //   2. We don't remove Dashboard view type for shorter paths because "dashboard" is also
        //      a path for the entire Dashboard scene. And we wanna keep it unchanged.
        //   3. Strictly speaking, we don't need to remove /dashboard view type at all, because
        //      we tend no never add it to the path. But I'm still keeping this logic here in case
        //      such string still slips into the pathname.
        parts.length > 2 &&
        (parts.at(-1) === ViewType.DASHBOARD || parts.at(-1) === ViewType.TIMELINE)
      ) {
        parts.pop();
      }
      if (view === ViewType.DASHBOARD) {
        history.push(`${parts.join("/")}/${ViewType.TIMELINE}`);
      } else {
        history.push(parts.join("/"));
      }
    },
    [history, view, location.pathname, analytics]
  );

  return (
    <Tabs
      activeKey={view}
      className={styles.tab}
      defaultActiveKey={ViewType.DASHBOARD}
      items={items}
      onChange={handleChange}
    />
  );
};

export default DashboardTabs;
